/**
 * PATH: packages/email-hub/src/feature/email-layout/EmailSidebarSync.tsx
 * FILE_ID: EMAIL-LAYOUT-SIDEBAR-SYNC-001
 * PURPOSE: Render the existing sidebar mailbox connection and sync states.
 */

import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { ConnectedMailboxesPanel } from '../components/ConnectedMailboxesPanel'
import { DeepSyncControl } from './DeepSyncControl'
import { SyncActivityPanel } from './SyncActivityPanel'
import type { EmailLayoutController } from './useEmailLayoutController'

export function EmailSidebarSync({
  layout,
}: {
  layout: EmailLayoutController
}) {
  return (
    <div
      className={cn(
        'border-t border-border',
        layout.collapsed ? 'p-1.5' : 'p-3',
      )}
    >
      {layout.collapsed ? (
        <button
          onClick={() => {
            if (layout.outlookConnected && !layout.outlookExpired) {
              void layout.handleSync()
            } else {
              void layout.connectOutlook()
            }
          }}
          disabled={layout.isSyncing}
          className={cn(
            'flex items-center justify-center h-9 w-9 mx-auto rounded-lg transition-colors disabled:opacity-50',
            layout.outlookConnected && !layout.outlookExpired
              ? 'text-emerald-600 dark:text-emerald-400 hover:bg-accent'
              : layout.outlookExpired
                ? 'text-red-600 dark:text-red-400 hover:bg-accent'
                : 'text-[#0078D4] hover:bg-accent',
          )}
          title={
            layout.outlookConnected && !layout.outlookExpired
              ? (layout.isSyncing ? 'Syncing...' : 'Sync Now')
              : layout.outlookExpired
                ? 'Reconnect Outlook (session expired)'
                : 'Connect Outlook'
          }
        >
          {layout.isSyncing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : layout.outlookConnected && !layout.outlookExpired ? (
            <RefreshCw className="w-4 h-4" />
          ) : layout.outlookExpired ? (
            <AlertTriangle className="w-4 h-4" />
          ) : (
            <ExternalLink className="w-4 h-4" />
          )}
        </button>
      ) : layout.outlookConnected === null ? (
        <p className="text-xs text-muted-foreground">Checking Outlook...</p>
      ) : layout.outlookConnected && !layout.outlookExpired ? (
        <div className="space-y-2">
          <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 shrink-0" />
            <span
              className="truncate"
              title={layout.outlook.email ?? 'Outlook Connected'}
            >
              {layout.outlook.email ?? 'Outlook Connected'}
            </span>
          </p>
          {layout.emailAccounts.length > 0 && (
            <ConnectedMailboxesPanel
              emailAccounts={layout.emailAccounts}
              syncStatus={layout.syncStatus}
              activeEmail={layout.outlook.email ?? layout.gmail.email}
              isSyncing={layout.isSyncing}
              onSyncAccount={layout.handleSyncAccount}
              onReconnect={layout.handleReconnectMailbox}
              onOpenSettings={layout.openSettings}
              currentUserId={layout.session?.user?.id ?? null}
              onSetPrivacy={layout.handleSetMailboxPrivacy}
            />
          )}
          <DeepSyncControl layout={layout} />
          <SyncActivityPanel layout={layout} />
        </div>
      ) : layout.outlookConnected && layout.outlookExpired ? (
        <div className="space-y-2">
          <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Session expired
          </p>
          <button
            onClick={() => { void layout.connectOutlook() }}
            className="w-full text-xs px-2 py-1.5 rounded-md bg-[#0078D4] text-white hover:bg-[#006CBE] transition-colors flex items-center justify-center gap-1.5"
          >
            <ExternalLink className="w-3 h-3" /> Reconnect
          </button>
        </div>
      ) : layout.hasSharedMailboxAccess ? (
        <div className="space-y-2">
          <p
            className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1"
            title={`Viewing ${layout.emailAccounts.length} shared mailbox${layout.emailAccounts.length === 1 ? '' : 'es'}`}
          >
            <CheckCircle2 className="w-3 h-3 shrink-0" />
            <span className="truncate">
              Shared inbox
              {layout.emailAccounts.length > 1
                ? `es (${layout.emailAccounts.length})`
                : ''}
            </span>
          </p>
          <ConnectedMailboxesPanel
            emailAccounts={layout.emailAccounts}
            syncStatus={layout.syncStatus}
            activeEmail={layout.emailAccounts[0]?.email_address ?? null}
            isSyncing={layout.isSyncing}
            onSyncAccount={layout.handleSyncAccount}
            onReconnect={layout.handleReconnectMailbox}
            onOpenSettings={layout.openSettings}
            currentUserId={layout.session?.user?.id ?? null}
            onSetPrivacy={layout.handleSetMailboxPrivacy}
          />
          <button
            onClick={() => { void layout.handleSync() }}
            disabled={layout.isSyncing}
            className="w-full text-xs px-2 py-1.5 rounded-md border border-border hover:bg-accent transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
            title="Sync the shared mailboxes"
          >
            {layout.isSyncing
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <RefreshCw className="w-3 h-3" />}
            {layout.isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Connect your mailbox to start reading and sending mail.
          </p>
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => { void layout.connectOutlook() }}
              className="w-full text-xs px-2 py-1.5 rounded-md bg-[#0078D4] text-white hover:bg-[#006CBE] transition-colors flex items-center justify-center gap-1.5"
              aria-label="Connect Microsoft Outlook"
            >
              <ExternalLink className="w-3 h-3" /> Connect Outlook
            </button>
            <button
              type="button"
              onClick={() => { void layout.gmail.authorize() }}
              className="w-full text-xs px-2 py-1.5 rounded-md bg-[#EA4335] text-white hover:bg-[#D33C2D] transition-colors flex items-center justify-center gap-1.5"
              aria-label="Connect Gmail"
            >
              <ExternalLink className="w-3 h-3" /> Connect Gmail
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
